import React, { useState, useEffect, useCallback } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import { WARRANTY_PERIODS } from '../utils/warranty';
import WarrantyBadge from '../components/WarrantyBadge';
import { Plus, RefreshCw, Edit2, Trash2, FileUp, FileText, X, Ban, Eye, History, Printer } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, MultiFilterSelect, PageHeader } from '../components/FilterBar';
import { DEREC_TYPES, calcWDV, fmtLKR, releasesAccessoryCustody, statusLabel } from '../utils/assetUtils';
import SearchableSelect from '../components/SearchableSelect';
import CompanyDeptFilter from '../components/CompanyDeptFilter';

const STATUSES = ['available','in_use','broken','lost','donated','disposed'];

export default function Accessories() {
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState([]); const [filterCat, setFilterCat] = useState('');
  const [derecognized, setDerecognized] = useState('');
  const [derecType, setDerecType] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState(null);
  const [filterDept, setFilterDept] = useState(null);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState(null); const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (r) => {
    setSelected(r); setHistoryLoading(true); setModal('history');
    const h = await api.get(`/accessories/${r.id}/history`).then(x => x.data)
      .catch(() => ({ accessory: r, audit: [], requests: [] }));
    setHistory(h); setHistoryLoading(false);
  };

  // Open the printable full history in a new tab. The window must be opened
  // synchronously in the click handler — after an await it is popup-blocked.
  const printHistory = (r) => {
    if (!r?.id) return;
    window.open(`/api/accessories/${r.id}/history/print?token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterStatus.length) params.status = filterStatus.join(','); if (filterCat) params.category = filterCat;
      if (derecognized) params.derecognized = derecognized;
      if (derecType) params.derecognition_type = derecType;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(",") : filterCompany;
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/accessories', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterCat, derecognized, derecType, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    // Load master category list
    api.get('/master/accessory-categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    if (!form.category) { setError('Category is required.'); return; }
    if (!form.purchased_date) { setError('Purchase Date is required.'); return; }
    if (!form.purchase_price) { setError('Purchase Price is required.'); return; }
    if (!form.warranty_period || form.warranty_period === 'none') { setError('Warranty Period is required. Select a period, "Warranty Void", or "Lifetime".'); return; }
    setSaving(true); setError('');
    try {
      selected ? await api.put(`/accessories/${selected.id}`, form) : await api.post('/accessories', form);
      setModal(null); setSelected(null); load();
      api.get('/master/accessory-categories').then(r => setCategories(r.data || [])).catch(() => {});
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'ACC #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.accessory_number||'—'}</span> },
    { header: 'Name', render: r => <span className="font-medium" style={{color:'#e2e8f0'}}>{r.name}</span> },
    { header: 'Category', render: r => <span className="text-xs px-2 py-0.5 rounded-full" style={{background:'rgba(99,102,241,0.15)',color:'#818cf8',border:'1px solid rgba(99,102,241,0.25)'}}>{r.category||'—'}</span> },
    { header: 'Brand / Model', render: r => `${r.brand||''} ${r.model||''}`.trim() || <span style={{color:'#334155'}}>—</span> },
    { header: 'Serial #', render: r => <span className="font-mono text-xs" style={{color:'#475569'}}>{r.serial_number||'—'}</span> },
    { header: 'Qty', render: r => <span style={{color:'#e2e8f0'}}>{r.quantity}</span> },
    { header: 'Assigned To', render: r => r.assignedEmployee
      ? <span style={{color:'#94a3b8'}}>{r.assignedEmployee.full_name}</span>
      // Released / derecognized accessories keep their last user on record
      : r.lastHolder
        ? <div>
            <div className="text-xs" style={{color:'#64748b'}}>Last used by</div>
            <div className="text-xs font-medium" style={{color:'#94a3b8'}}>{r.lastHolder.full_name}</div>
            {r.last_held_to && <div className="text-xs" style={{color:'#475569'}}>until {new Date(r.last_held_to).toLocaleDateString('en-GB')}</div>}
          </div>
        : <span style={{color:'#94a3b8'}}>—</span>
    },
    { header: 'Status / WDV', render: r => {
      const wdv = r.wdv != null ? r.wdv : calcWDV(r.purchase_price, r.purchased_date);
      const depVal = r.depreciated_value;
      const daysRemaining = r.wdv_days_remaining;
      return (
        <div className="space-y-0.5">
          <Badge value={r.status}/>
          {r.derecognized && <div className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(239,68,68,0.15)',color:'#f87171'}}><Ban size={9}/>Derecognized</div>}
          {r.age_days != null && <div className="text-xs" style={{color:'#334155'}}>{r.age_days >= 365 ? `${Math.floor(r.age_days/365)}y ${Math.floor((r.age_days%365)/30)}mo` : `${Math.floor(r.age_days/30)}mo`} ({r.age_days}d)</div>}
          {wdv != null && <div className="text-xs font-mono" style={{color: wdv === 0 ? '#ef4444' : '#34d399'}}>WDV: {fmtLKR(wdv)}</div>}
          {depVal != null && <div className="text-xs font-mono" style={{color:'#f97316'}}>Dep: {fmtLKR(depVal)}</div>}
          {daysRemaining != null && daysRemaining > 0 && <div className="text-xs" style={{color:'#64748b'}}>{daysRemaining}d left</div>}
        </div>
      );
    }},
    { header: '', render: r => (
      <div className="flex gap-1">
        <button onClick={() => { setSelected(r); setModal('view'); }} className="btn-ghost px-2 py-1 text-xs" title="View" style={{color:'#60a5fa'}}><Eye size={11}/></button>
        {isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
        {isIT && <button onClick={() => openHistory(r)} className="btn-ghost px-2 py-1 text-xs" title="Full History"><History size={11}/></button>}
        {isAdmin && <button onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`/accessories/${r.id}`); load(); }}} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}}><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Accessories" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({quantity:1,status:'available'}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Accessory</button>}
        </div>
      </PageHeader>
      <FilterBar hasFilters={!!(search||filterStatus.length||filterCat||derecognized||derecType||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus([]); setFilterCat(''); setDerecognized(''); setDerecType(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search name, brand, model, serial..."/>
        <FilterSelect value={filterCat} onChange={v => { setFilterCat(v); setPage(1); }} placeholder="All Categories" options={categories.map(c=>({value:c,label:c}))}/>
        <MultiFilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s=>({value:s,label:s}))}/>
        <FilterSelect value={derecognized} onChange={v => { setDerecognized(v); setDerecType(''); setPage(1); }} placeholder="Derecognition: All" options={[{value:'false',label:'Active Only'},{value:'true',label:'Derecognized Only'}]}/>
        <FilterSelect value={derecType} onChange={v => { setDerecType(v); setDerecognized(''); setPage(1); }} placeholder="Disposal Type: All" options={DEREC_TYPES.map(t=>({value:t,label:t.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}))}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>
      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {modal === 'form' && (
        <Modal title={selected ? 'Edit Accessory' : 'Add Accessory'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Accessory # <span style={{color:'#334155'}}>(auto-generated)</span></label>
                <input value={form.accessory_number||'Auto'} readOnly className="input-dark" style={{opacity:0.6,cursor:'not-allowed'}}/>
              </div>
              <FF label="Name *" name="name" form={form} setForm={setForm} required placeholder="e.g. Laptop Bag, USB Hub"/>
              {/* Category — from master list, prevents duplicates */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category *</label>
                <SearchableSelect required value={form.category||''} onChange={v=>setForm({...form,category:v})}
                  options={categories.map(c=>({value:c,label:c}))} placeholder="Select category"/>
                <div className="text-xs mt-1" style={{color:'#334155'}}>Don't see your category? Type it in Name and it will be available next time.</div>
              </div>
              <FF label="Brand" name="brand" form={form} setForm={setForm}/>
              <FF label="Model" name="model" form={form} setForm={setForm}/>
              <FF label="Serial Number" name="serial_number" form={form} setForm={setForm}/>
              <FF label="Quantity" name="quantity" type="number" form={form} setForm={setForm}/>
              <div>
                <FS label="Status" name="status" form={form} setForm={setForm} options={STATUSES.map(s=>({val:s,label:s}))}/>
                {releasesAccessoryCustody(form.status) && selected?.assigned_to && (
                  <div className="text-xs mt-1" style={{color:'#fbbf24'}}>
                    Saving will release this accessory from <strong>{selected.assignedEmployee?.full_name || 'its current holder'}</strong> — it can no longer be allocated while marked {statusLabel(form.status)}.
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Assigned To</label>
                {form.assigned_to && !employees.find(e => e.id === form.assigned_to) && selected?.assignedEmployee && (
                  <div className="mb-1.5 px-3 py-1.5 rounded-lg text-xs flex items-center gap-2" style={{background:'rgba(251,146,60,0.1)',border:'1px solid rgba(251,146,60,0.3)',color:'#fb923c'}}>
                    ⚠ <strong>{selected.assignedEmployee.full_name}</strong> has resigned — please reassign
                  </div>
                )}
                <SearchableSelect value={form.assigned_to||''} onChange={v=>setForm({...form,assigned_to:v})}
                  options={employees.map(e=>({value:e.id,label:e.full_name}))} placeholder="Not assigned"/>
              </div>
              <FF label="Purchase Price (LKR) *" name="purchase_price" type="number" form={form} setForm={setForm} required/>
              <FF label="Purchase Date *" name="purchased_date" type="date" form={form} setForm={setForm} required/>
              <FF label="Vendor" name="vendor" form={form} setForm={setForm}/>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color: (!form.warranty_period || form.warranty_period === 'none') ? '#ef4444' : '#475569'}}>
                  Warranty Period *
                </label>
                <select required value={form.warranty_period||'none'} onChange={e=>setForm({...form,warranty_period:e.target.value})}
                  className="input-dark"
                  style={{borderColor: (!form.warranty_period || form.warranty_period === 'none') ? '#ef4444' : ''}}>
                  {WARRANTY_PERIODS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {form.warranty_period && form.warranty_period !== 'none' && form.purchased_date && (
                  <div className="mt-1.5"><WarrantyBadge warrantyPeriod={form.warranty_period} purchasedDate={form.purchased_date}/></div>
                )}
              </div>
            </div>
            {/* Derecognition */}
            <div className="rounded-xl p-4" style={{background:'rgba(239,68,68,0.04)',border:'1px solid rgba(239,68,68,0.2)'}}>
              <div className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{color:'#f87171'}}><Ban size={11}/> Derecognition</div>
              <label className="flex items-center gap-2 cursor-pointer text-sm mb-3" style={{color:'#e2e8f0'}}>
                <input type="checkbox" checked={!!form.derecognized} onChange={e=>setForm({...form,derecognized:e.target.checked})} className="accent-red-500 w-4 h-4"/>
                Mark as Derecognized — remove from active register
              </label>
              {form.derecognized && (
                <div className="grid grid-cols-3 gap-3 mt-1">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Disposal Type</label>
                    <select value={form.derecognition_type||''} onChange={e=>setForm({...form,derecognition_type:e.target.value})} className="input-dark">
                      <option value="">Select type...</option>
                      {DEREC_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
                    </select>
                  </div>
                  <FF label="Disposal Date" name="derecognition_date" type="date" form={form} setForm={setForm}/>
                  <FF label="Approved By *" name="derecognition_approved_by" form={form} setForm={setForm} placeholder="Name(s) of approving person(s)"/>
                  {form.derecognition_type === 'sold' && (
                    <FF label="Sale Value (LKR)" name="sale_value" type="number" form={form} setForm={setForm} placeholder="0"/>
                  )}
                  {form.purchase_price && form.purchased_date && (
                    <div className="col-span-3 p-3 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
                      <div className="text-xs font-semibold mb-1" style={{color:'#94a3b8'}}>Accounting Summary at Disposal</div>
                      <div className="flex gap-6 text-xs flex-wrap">
                        <div><span style={{color:'#475569'}}>Purchase Price: </span><span style={{color:'#e2e8f0'}}>{fmtLKR(form.purchase_price)}</span></div>
                        <div><span style={{color:'#475569'}}>WDV at Disposal: </span><span style={{color:'#34d399'}}>{fmtLKR(calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date))}</span></div>
                        {form.derecognition_type === 'sold' && form.sale_value != null && form.sale_value !== '' && (
                          <div>
                            <span style={{color:'#475569'}}>Gain / Loss: </span>
                            <span style={{color: (parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date)) >= 0 ? '#34d399' : '#f87171', fontWeight:700}}>
                              {fmtLKR(parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date))}
                              {' '}{(parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date)) >= 0 ? '(Gain)' : '(Loss)'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Insurance Coverage */}
            <div className="rounded-xl p-4" style={{background:'rgba(59,130,246,0.04)',border:'1px solid rgba(59,130,246,0.15)'}}>
              <div className="text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>Insurance Coverage</div>
              <div className="flex gap-6 mb-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{color:'#e2e8f0'}}>
                  <input type="checkbox" checked={!!form.insured_local} onChange={e=>setForm({...form,insured_local:e.target.checked})} className="accent-blue-500 w-4 h-4"/>
                  Local Insurance
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{color:'#e2e8f0'}}>
                  <input type="checkbox" checked={!!form.insured_overseas} onChange={e=>setForm({...form,insured_overseas:e.target.checked})} className="accent-purple-500 w-4 h-4"/>
                  Overseas Insurance
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#f59e0b'}}>
                  Finance Team Email(s) for Insurance Notification
                  <span className="ml-1 font-normal" style={{color:'#475569'}}>(comma-separated — email with invoice sent when invoice is uploaded)</span>
                </label>
                <input
                  type="text"
                  value={form.finance_emails||''}
                  onChange={e=>setForm({...form,finance_emails:e.target.value})}
                  className="input-dark"
                  placeholder="finance@clsynergy.com, insurance@clsynergy.com"
                />
              </div>
            </div>

            {/* Supplier Invoice */}
            <div className="rounded-xl p-4" style={{background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.2)'}}>
              <div className="text-xs font-semibold mb-2 flex items-center gap-2" style={{color:'#fbbf24'}}><FileUp size={12}/> Supplier Invoice</div>
              {selected?.invoice_filename ? (
                <div className="flex items-center gap-2">
                  <FileText size={13} style={{color:'#fbbf24'}}/>
                  <a href={selected.invoice_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{color:'#60a5fa'}}>{selected.invoice_filename}</a>
                  <span className="text-xs" style={{color:'#334155'}}>(click to view/download)</span>
                  {isIT && <button type="button" onClick={async () => { await api.delete(`/accessories/${selected.id}/invoice`); load(); setModal(null); setSelected(null); }} className="ml-auto text-xs px-2 py-0.5 rounded" style={{color:'#ef4444',background:'rgba(239,68,68,0.1)'}}><X size={11}/> Remove</button>}
                </div>
              ) : (
                <div className="text-xs" style={{color:'#334155'}}>No invoice uploaded yet.</div>
              )}
              {isIT && selected?.id && (
                <div className="mt-2">
                  <label className="text-xs" style={{color:'#475569'}}>Upload Invoice (PDF, image — max 10MB)</label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="mt-1 w-full text-xs" style={{color:'#94a3b8'}}
                    onChange={async e => {
                      if (!e.target.files[0]) return;
                      const fd = new FormData(); fd.append('invoice', e.target.files[0]);
                      await api.post(`/accessories/${selected.id}/invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      load(); setModal(null); setSelected(null);
                    }}/>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
      {modal === 'view' && selected && (
        <Modal title={`Accessory — ${selected.accessory_number || selected.name || `${selected.brand} ${selected.model}`}`} onClose={() => { setModal(null); setSelected(null); }}>
          <div className="space-y-3">
            {[
              ['Acc #', selected.accessory_number||'—'],
              ['Name', selected.name||'—'],
              ['Category', selected.category||'—'],
              ['Brand / Model', `${selected.brand||''} ${selected.model||''}`.trim()||'—'],
              ['Serial No', selected.serial_number||'—'],
              ['Status', <Badge value={selected.status}/>],
              ['Assigned To', selected.assignedEmployee?.full_name||'—'],
              ['Purchase Date', selected.purchased_date||'—'],
              ['Allocated Date', selected.allocated_date||'—'],
              ['Warranty Period', selected.warranty_period && selected.warranty_period !== 'none' ? selected.warranty_period.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) : '—'],
              ['Warranty Expiry', selected.warranty_expiry_date||'—'],
              ['Purchase Price', selected.purchase_price ? `LKR ${Number(selected.purchase_price).toLocaleString()}` : '—'],
              ['Vendor', selected.vendor||'—'],
              ['Age', selected.age_days != null ? `${Math.floor(selected.age_days/365)}y ${Math.floor((selected.age_days%365)/30)}mo (${selected.age_days}d)` : '—'],
              ['WDV', selected.wdv != null ? `LKR ${Number(selected.wdv).toLocaleString()}` : '—'],
              ['Depreciated Value', selected.depreciated_value != null ? `LKR ${Number(selected.depreciated_value).toLocaleString()}` : '—'],
              ['Remarks', selected.remarks||'—'],
            ].filter(([,v]) => v && v !== '—').map(([k,v]) => (
              <div key={k} className="flex items-start gap-3 py-2" style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                <span className="text-xs flex-shrink-0 w-32" style={{color:'#475569'}}>{k}</span>
                <span className="text-xs flex-1" style={{color:'#e2e8f0'}}>{v}</span>
              </div>
            ))}
            {selected.derecognized && (
              <div className="rounded-xl p-3 text-xs" style={{background:'rgba(239,68,68,0.07)',border:'1px solid rgba(239,68,68,0.2)'}}>
                <span style={{color:'#f87171',fontWeight:600}}>Derecognized</span>
                {selected.derecognition_type && <span style={{color:'#475569'}}> · {selected.derecognition_type}</span>}
                {selected.derecognition_date && <span style={{color:'#475569'}}> · {selected.derecognition_date}</span>}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── FULL HISTORY MODAL ─────────────────────────────── */}
      {modal === 'history' && (
        <Modal
          title={`Full History — ${selected?.accessory_number || selected?.name || ''}`}
          onClose={() => { setModal(null); setHistory(null); }} size="lg">
          {historyLoading ? (
            <div className="flex items-center justify-center h-24 gap-2" style={{color:'#334155'}}>
              <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>Loading...
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 pb-3" style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                <div className="text-xs" style={{color:'#475569'}}>
                  Complete lifecycle record — originating request, every allocation, return and change.
                </div>
                <button onClick={() => printHistory(selected)} className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap">
                  <Printer size={12}/>&nbsp;Print / Save as PDF
                </button>
              </div>

              <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
                {/* Current state */}
                <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                  <div className="text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>Current State</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    {[
                      ['Status', <Badge key="s" value={history?.accessory?.status}/>],
                      ['Holder', history?.accessory?.holder_name || 'Unassigned'],
                      ['Company', history?.accessory?.company_name || '—'],
                      ['Department', history?.accessory?.department_name || '—'],
                      ['Allocated', history?.accessory?.allocated_date || '—'],
                      ['Linked Asset', history?.accessory?.linked_asset_number || '—'],
                      ['Purchased', history?.accessory?.purchased_date || '—'],
                      ['Purchase Price', fmtLKR(history?.accessory?.purchase_price)],
                    ].map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="flex-shrink-0" style={{color:'#475569',minWidth:88}}>{k}</span>
                        <span style={{color:'#e2e8f0'}}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Originating requests */}
                <div>
                  <div className="text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>
                    Originating Requests ({(history?.requests||[]).length})
                  </div>
                  {!(history?.requests||[]).length ? (
                    <div className="text-center py-4 text-xs" style={{color:'#1e3a5f'}}>Not created from an accessory request</div>
                  ) : history.requests.map((r, i) => (
                    <div key={r.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(139,92,246,0.05)',border:'1px solid rgba(139,92,246,0.15)'}}>
                      <div className="flex items-center justify-between gap-2">
                        <span style={{color:'#e2e8f0'}}>
                          <span className="font-mono" style={{color:'#a78bfa'}}>{r.request_number}</span>
                          {r.employee_name ? ` — ${r.employee_name}` : ''}
                        </span>
                        <Badge value={r.status}/>
                      </div>
                      <div className="mt-1" style={{color:'#94a3b8'}}>
                        {[...(Array.isArray(r.requested_items) ? r.requested_items : []), r.other_item].filter(Boolean).join(', ') || '—'}
                      </div>
                      <div className="mt-1" style={{color:'#475569'}}>
                        Raised {new Date(r.created_at).toLocaleString('en-GB')}
                        {r.approved_by ? ` · Approved by ${r.approved_by}` : ''}
                      </div>
                      {r.reason && <div className="mt-1" style={{color:'#334155'}}>{r.reason}</div>}
                      {r.rejection_reason && <div className="mt-1" style={{color:'#f87171'}}>Reason: {r.rejection_reason}</div>}
                    </div>
                  ))}
                </div>

                {/* Audit trail — the accessory's allocation and change log */}
                <div>
                  <div className="text-xs font-semibold mb-1" style={{color:'#94a3b8'}}>
                    Allocation &amp; Change Log ({(history?.audit||[]).length})
                  </div>
                  <div className="text-xs mb-2" style={{color:'#334155'}}>
                    Accessories have no separate assignment table — every allocation, return and status change is recorded here.
                  </div>
                  {!(history?.audit||[]).length ? (
                    <div className="text-center py-4 text-xs" style={{color:'#1e3a5f'}}>No log entries</div>
                  ) : (
                    <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(255,255,255,0.06)'}}>
                      {history.audit.map((x, i) => (
                        <div key={x.id||i} className="flex items-start gap-2 px-3 py-2 text-xs"
                          style={{background: i % 2 ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                          <span className="flex-shrink-0" style={{color:'#475569',minWidth:118}}>
                            {new Date(x.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'2-digit',hour:'2-digit',minute:'2-digit'})}
                          </span>
                          <span className="flex-shrink-0" style={{color:'#60a5fa',minWidth:52}}>{x.action}</span>
                          <span className="flex-shrink-0" style={{color:'#94a3b8',minWidth:110}}>{x.user_name||x.username||'—'}</span>
                          <span style={{color:'#334155'}}>{x.description||'—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
